import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import axios from 'axios';
import { API } from '../utils/api';

// Fully isolated auth context for the SUPERADMIN role.
// Token is stored under a separate localStorage key so it can never clash
// with the org or community auth tokens, and we always pass it explicitly
// in Authorization headers (we never set axios.defaults globally for it).
const SuperadminAuthContext = createContext(null);
const SUPERADMIN_TOKEN_KEY = 'superadmin_token';

export const useSuperadminAuth = () => {
  const ctx = useContext(SuperadminAuthContext);
  if (!ctx) throw new Error('useSuperadminAuth must be used within SuperadminAuthProvider');
  return ctx;
};

export const SuperadminAuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem(SUPERADMIN_TOKEN_KEY));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async (t) => {
    try {
      const res = await axios.get(`${API}/superadmin/me`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setUser(res.data);
    } catch {
      localStorage.removeItem(SUPERADMIN_TOKEN_KEY);
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) fetchMe(token);
    else setLoading(false);
  }, [token, fetchMe]);

  const login = (newToken, userData) => {
    localStorage.setItem(SUPERADMIN_TOKEN_KEY, newToken);
    setToken(newToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem(SUPERADMIN_TOKEN_KEY);
    setToken(null);
    setUser(null);
  };

  // Helper so pages can make authed calls without repeating header config.
  const authedRequest = useCallback(
    (method, url, opts = {}) => {
      return axios.request({
        method,
        url: `${API}${url}`,
        ...opts,
        headers: {
          ...(opts.headers || {}),
          Authorization: `Bearer ${token}`,
        },
      });
    },
    [token]
  );

  return (
    <SuperadminAuthContext.Provider value={{ token, user, loading, login, logout, authedRequest }}>
      {children}
    </SuperadminAuthContext.Provider>
  );
};

export default SuperadminAuthContext;
